import type { AuthAdapter, AuthSession } from './types';
import type { RawGrant } from '../permissions/types';

export interface MockPersona {
  readonly id: string;
  readonly displayName: string;
  readonly grants: readonly RawGrant[];
}

const STORAGE_KEY = 'eis.mock-auth.persona';

/**
 * The demo tenant is POOLED, because the pooled case is the one every screen has to render well:
 * a silo tenant sees the same console with a different badge, and a mock that defaulted to SILO
 * would make the rarer layout the one everybody develops against.
 */
const DEFAULT_TENANT = {
  id: 'tenant-demo',
  slug: 'demo',
  name: 'EIS Demo',
  placement: 'POOL',
  location: 'shared_db',
} as const;

/**
 * Mock-first auth (backbone §7, "the same compiled bundle runs everywhere").
 *
 * This is the adapter dev, Storybook, unit tests and BDD run against; it produces sessions with
 * the exact wire shape `parseTokenResponse` produces in production, so swapping in the real OIDC
 * adapter changes no consumer. Persona selection persists so a reload keeps the chosen role.
 */
export function createMockAuthAdapter(
  personas: Readonly<Record<string, MockPersona>>,
  defaultPersonaId: string,
): AuthAdapter {
  const buildSession = (personaId: string): AuthSession => {
    const persona = personas[personaId] ?? personas[defaultPersonaId];
    if (persona === undefined) {
      throw new Error(`Unknown mock persona "${personaId}" and no default persona available.`);
    }
    return {
      user: {
        id: `user-${persona.id}`,
        email: `${persona.id}@example.test`,
        displayName: persona.displayName,
        role: persona.id,
      },
      tenant: DEFAULT_TENANT,
      grants: persona.grants,
      accessToken: `mock.${persona.id}.token`,
      expiresAt: Date.now() + 60 * 60 * 1000,
    };
  };

  const readPersonaId = (): string => {
    try {
      return globalThis.localStorage?.getItem(STORAGE_KEY) ?? defaultPersonaId;
    } catch {
      return defaultPersonaId;
    }
  };

  const writePersonaId = (personaId: string): void => {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, personaId);
    } catch {
      /* storage unavailable (SSR-ish test env) — the in-memory session still works */
    }
  };

  return {
    kind: 'mock',
    signIn: (hint) => {
      const personaId = hint ?? readPersonaId();
      writePersonaId(personaId);
      return Promise.resolve(buildSession(personaId));
    },
    signOut: () => {
      try {
        globalThis.localStorage?.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      return Promise.resolve();
    },
    restore: () => Promise.resolve(buildSession(readPersonaId())),
    refresh: () => Promise.resolve(buildSession(readPersonaId())),
    // A real token would be refreshed here; the mock's never expires, so this is the same
    // seam with none of the machinery — which is the point of the mock adapter existing.
    getAccessToken: () => Promise.resolve(buildSession(readPersonaId()).accessToken),
  };
}
